// client/src/pages/SignedInStudent/SignedInStudent.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import "./SignedInStudent.css";
import axios from "axios";
import { COPY, useToast } from "../../context/ToastContext";

const formatSignInTime = (signInTime) => {
  const date = new Date(signInTime);
  return date.toLocaleString();
};

const calculateDurationMs = (signInTime) => {
  const signInDate = new Date(signInTime);
  const now = new Date();
  return now - signInDate;
};

const formatDuration = (milliseconds) => {
  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  minutes = minutes % 60;
  seconds = seconds % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${seconds}s`;
};

const calculateDuration = (signInTime) => {
  const duration = calculateDurationMs(signInTime);
  return formatDuration(duration);
};

// Both actions fail the same way, so they share one classifier.
const describeFailure = (error) => {
  if (!error.response) return ["error", COPY.unreachable];
  if (error.response.status === 401) return ["warning", COPY.sessionExpired];
  return null;
};

export default function SignedInStudent() {
  const { toast } = useToast();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchStudents = useCallback(async () => {
    try {
      const response = await axios.get(`/api/signinTracker?action=getAll`, {
        withCredentials: true,
      });
      setData(
        response.data.map((student) => ({
          ...student,
          formattedSignInTime: formatSignInTime(student.SignInTime),
          duration: calculateDuration(student.SignInTime),
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const SignAllOut = async () => {
    // Count before the request, since the list is what we are about to empty.
    const count = data.length;
    if (count === 0) {
      toast.info("Nobody is signed in right now.");
      return;
    }

    try {
      await axios.delete(`/api/signinTracker?action=signOutAll`, {
        withCredentials: true,
      });
      fetchStudents();
      toast.success(
        count === 1 ? "1 student signed out." : `All ${count} students signed out.`
      );
    } catch (error) {
      console.error("Error signing all out:", error);
      const [variant, message] =
        describeFailure(error) ?? ["error", "Couldn't sign everyone out. Please try again."];
      toast[variant](message);
    }
  };

  const signOutById = async (studentId, name) => {
    const firstName = name ? name.split(" ")[0] : "That student";

    try {
      await axios.delete(`/api/signinTracker?action=signOut&id=${studentId}`, {
        withCredentials: true,
      });
      fetchStudents();
      toast.success(`${firstName} is signed out.`);
    } catch (error) {
      console.error("Error signing out student:", error);

      // 404 means they were already out, which is the state the admin wanted.
      if (error.response?.status === 404) {
        toast.info(`${firstName} was already signed out.`);
        fetchStudents();
        return;
      }

      const [variant, message] =
        describeFailure(error) ?? ["error", `Couldn't sign ${firstName} out. Please try again.`];
      toast[variant](message);
    }
  };

  const exportToCSV = () => {
    const headers = "Name,Signed In,Duration\n";
    const rows = data
      .map(
        (student) =>
          `"${student.name}","${student.formattedSignInTime}","${student.duration}"`
      )
      .join("\n");
    const csv = headers + rows;
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "signed_in_students.csv";
    link.click();
  };

  const filteredData = data.filter((student) =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const longestSession = useMemo(() => {
    if (!data.length) return "--";
    const { SignInTime } = data.reduce((longest, current) => {
      const currentDuration = calculateDurationMs(current.SignInTime);
      return currentDuration > calculateDurationMs(longest.SignInTime)
        ? current
        : longest;
    });
    return formatDuration(calculateDurationMs(SignInTime));
  }, [data]);

  const totalStudents = data.length;

  return (
    <div className="signedInStudent">
      <div className="page-shell">
        <header className="dashboard-hero">
          <div>
            <p className="eyebrow">Live dashboard</p>
            <h1 className="hero-title">Signed-In Students</h1>
            <p className="hero-subtitle">
              Track active sessions, export attendance, and quickly sign out
              students when they leave the shop.
            </p>
            <div className="hero-actions">
              <button className="ghost-btn" onClick={fetchStudents}>
                Refresh list
              </button>
              <button className="ghost-btn" onClick={exportToCSV}>
                Export CSV
              </button>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-label">Currently signed in</span>
            <div className="stat-value">{totalStudents}</div>
            <div className="stat-meta">Longest session: {longestSession}</div>
          </div>
        </header>

        <section className="controls-row">
          <div className="search">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Search by student name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="actions">
            <button className="pill-btn" onClick={SignAllOut}>
              <i className="fas fa-sign-out-alt"></i>
              Sign all out
            </button>
            <button className="pill-btn" onClick={fetchStudents}>
              <i className="fas fa-rotate"></i>
              Refresh
            </button>
            <button className="pill-btn" onClick={exportToCSV}>
              <i className="fas fa-file-export"></i>
              Export CSV
            </button>
          </div>
        </section>

        <div className="panel">
          <div className="panel-header">
            <div>
              <p className="panel-label">Active roster</p>
              <h2 className="panel-title">
                {filteredData.length} student{filteredData.length === 1 ? "" : "s"}
              </h2>
            </div>
          </div>

          {filteredData.length > 0 ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Signed In</th>
                    <th>Duration</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((student) => (
                    <tr key={student.studentID}>
                      <td>
                        <div className="name-cell">
                          <span className="name">{student.name}</span>
                        </div>
                      </td>
                      <td>{student.formattedSignInTime}</td>
                      <td>
                        <span className="duration-chip">{student.duration}</span>
                      </td>
                      <td>
                        <button
                          className="signout-individual-btn"
                          onClick={() => signOutById(student.studentID, student.name)}
                        >
                          Sign out
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <i className="fas fa-users-slash" aria-hidden="true"></i>
              <p>No students match your search.</p>
              <button className="ghost-btn" onClick={fetchStudents}>
                Reload list
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
